import {
    AcceptJSResponse,
    AuthorizeNetSecureData,
    CardData,
} from "../../modules/clients/types/clients.types";
import { useFetchAuthorizeClientPracticeSettings } from "../../modules/practice-settings/hooks/queries/practice-settings";
import useToast from "../useToast";

export type AcceptJSValidationField =
    | "cardNumber"
    | "expirationDate"
    | "cardCode"
    | "zip"
    | "fullName"
    | "payment";

export interface AcceptJSValidationIssue {
    field: AcceptJSValidationField;
    message: string;
}

interface AcceptJS {
    dispatchData(
        secureData: AuthorizeNetSecureData,
        responseHandler: (response: AcceptJSResponse) => void
    ): void;
}

declare const Accept: AcceptJS;

export class AcceptJSValidationError extends Error {
    issues: AcceptJSValidationIssue[];

    constructor(issues: AcceptJSValidationIssue[]) {
        super(issues[0]?.message ?? "Payment card details are invalid");
        this.name = "AcceptJSValidationError";
        this.issues = issues;
    }
}

export function isAcceptJSValidationError(
    error: unknown
): error is AcceptJSValidationError {
    return error instanceof AcceptJSValidationError;
}

const validationErrorCodes: Record<
    string,
    { field: AcceptJSValidationField; message: string }
> = {
    E_WC_04: {
        field: "payment",
        message: "Complete the required payment card details.",
    },
    E_WC_05: {
        field: "cardNumber",
        message: "Enter a valid card number.",
    },
    E_WC_06: {
        field: "expirationDate",
        message: "Enter a valid expiration date.",
    },
    E_WC_07: {
        field: "expirationDate",
        message: "Enter a valid expiration date.",
    },
    E_WC_08: {
        field: "expirationDate",
        message: "Enter an expiration date in the future.",
    },
    E_WC_15: {
        field: "cardCode",
        message: "Enter a valid security code.",
    },
    E_WC_16: {
        field: "zip",
        message: "Enter a valid ZIP code.",
    },
    E_WC_17: {
        field: "fullName",
        message: "Enter a valid cardholder name.",
    },
};

const paymentUnavailableMessage =
    "Payment processing is unavailable. Please refresh and try again.";

function getValidationIssueFromMessage(
    code: string,
    text: string
): AcceptJSValidationIssue | null {
    const codedValidation = validationErrorCodes[code];

    if (codedValidation) {
        return codedValidation;
    }

    const normalizedText = text.toLowerCase();

    if (
        normalizedText.includes("credit card number") ||
        normalizedText.includes("card number") ||
        normalizedText.includes("card element")
    ) {
        return validationErrorCodes.E_WC_05;
    }

    if (
        normalizedText.includes("expiration") ||
        normalizedText.includes("expiry")
    ) {
        return validationErrorCodes.E_WC_06;
    }

    if (
        normalizedText.includes("field length is invalid") ||
        normalizedText.includes("actual length")
    ) {
        return {
            field: "payment",
            message: "Check the payment card details and try again.",
        };
    }

    return null;
}

function getAcceptJSValidationIssues(
    response: AcceptJSResponse
): AcceptJSValidationIssue[] {
    return response.messages.message.reduce<AcceptJSValidationIssue[]>(
        (issues, message) => {
            const issue = getValidationIssueFromMessage(
                message.code,
                message.text
            );

            if (!issue) {
                return issues;
            }

            const existingIssue = issues.some(
                (currentIssue) =>
                    currentIssue.field === issue.field &&
                    currentIssue.message === issue.message
            );

            return existingIssue ? issues : [...issues, issue];
        },
        []
    );
}

export const useAcceptJS = () => {
    const { data } = useFetchAuthorizeClientPracticeSettings();
    const { toast } = useToast();

    const clientKey = data?.data?.settings?.find(
        (setting) => setting.setting_name === "authorize_net_public_client_key"
    )?.current_value;
    const apiLoginID = data?.data?.settings?.find(
        (setting) => setting.setting_name === "authorize_net_api_login_id"
    )?.current_value;

    const tokenizeCard = async (
        cardData: CardData
    ): Promise<AcceptJSResponse> => {
        if (!clientKey || !apiLoginID) {
            const error = new Error(paymentUnavailableMessage);
            toast({ mode: "error", message: error.message });
            throw error;
        }

        const secureData: AuthorizeNetSecureData = {
            authData: {
                apiLoginID,
                clientKey,
            },
            cardData: {
                cardNumber: cardData.cardNumber,
                month: cardData.month,
                year: cardData.year,
                cardCode: cardData.cardCode,
                zip: cardData.zip,
                fullName: cardData.fullName,
            },
        };

        return new Promise((resolve, reject) => {
            if (typeof Accept === "undefined") {
                reject(new Error(paymentUnavailableMessage));
                return;
            }

            Accept.dispatchData(secureData, (response: AcceptJSResponse) => {
                if (response.messages.resultCode === "Ok") {
                    resolve(response);
                    return;
                }

                const validationIssues = getAcceptJSValidationIssues(response);

                if (validationIssues.length > 0) {
                    reject(new AcceptJSValidationError(validationIssues));
                    return;
                }

                const errorMessage =
                    response.messages.message
                        .map((message) => message.text)
                        .join(" ") || "Payment tokenization failed.";

                reject(new Error(errorMessage));
            });
        }).catch((error: Error) => {
            if (isAcceptJSValidationError(error)) {
                throw error;
            }

            toast({ mode: "error", message: error.message });
            throw error;
        }) as Promise<AcceptJSResponse>;
    };

    return { tokenizeCard, isReady: !!clientKey && !!apiLoginID };
};
